import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminLogin,
  adminVerifyOTP,
  adminResendOTP,
  isAdminLoggedIn,
  adminPasskeyRegister,
  adminPasskeyAuthenticate,
  isWebAuthnSupported,
} from '../api/api';
import { identifyAdmin, resetPosthog } from '../analytics/posthog';
import './AdminLogin.css';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp' | 'passkey'
  const [adminEmail, setAdminEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [webauthnTicket, setWebauthnTicket] = useState('');
  const [passkeyStage, setPasskeyStage] = useState('auth'); // 'register' | 'auth'
  
  const otpRefs = useRef([]);

  useEffect(() => {
    if (isAdminLoggedIn()) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await adminLogin(email, password);
      
      if (response.data.success && response.data.requires_otp) {
        setAdminEmail(response.data.email);
        setStep('otp');
        setResendCooldown(60);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await adminVerifyOTP(adminEmail, otpString);

      if (response.data.success) {
        // Passkey factor enabled: don't have a token yet — run the WebAuthn step.
        if (response.data.requires_passkey) {
          setWebauthnTicket(response.data.webauthn_ticket);
          setPasskeyStage(response.data.passkey_stage || 'auth');
          setStep('passkey');
          return;
        }
        completeAdminLogin(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const completeAdminLogin = (data) => {
    localStorage.setItem('adminToken', data.access_token);
    localStorage.setItem('adminId', data.admin_id);
    localStorage.setItem('adminEmail', data.email);
    localStorage.setItem('adminName', data.name);
    localStorage.setItem('adminRole', data.role);

    resetPosthog();
    identifyAdmin(data.admin_id);

    navigate('/admin');
  };

  const handlePasskey = async () => {
    setError('');

    if (!isWebAuthnSupported()) {
      setError('This browser/device does not support passkeys. Use the device where you set it up.');
      return;
    }

    setLoading(true);
    try {
      const data =
        passkeyStage === 'register'
          ? await adminPasskeyRegister(webauthnTicket, navigator.platform || 'My laptop')
          : await adminPasskeyAuthenticate(webauthnTicket);

      if (data?.access_token) {
        completeAdminLogin(data);
      } else {
        throw new Error('Passkey step did not return a session.');
      }
    } catch (err) {
      // NotAllowedError = user cancelled or no matching authenticator on this device.
      const msg =
        err?.response?.data?.detail ||
        (err?.name === 'NotAllowedError'
          ? 'Passkey was cancelled or this is not the registered device.'
          : err?.message) ||
        'Passkey verification failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    
    setError('');
    setLoading(true);

    try {
      await adminResendOTP(adminEmail);
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setOtp(['', '', '', '', '', '']);
    setWebauthnTicket('');
    setPasskeyStage('auth');
    setError('');
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        <div className="admin-login-header">
          <img src="/logo.png" alt="Milan AI" className="admin-logo" />
          <h1>Admin Portal</h1>
          <p>
            {step === 'credentials' && 'Sign in to access the admin dashboard'}
            {step === 'otp' && 'Enter the OTP sent to your email'}
            {step === 'passkey' &&
              (passkeyStage === 'register'
                ? 'Register this device as your admin passkey'
                : 'Verify with your device passkey')}
          </p>
        </div>

        {step === 'credentials' && (
          <form onSubmit={handleCredentialsSubmit} className="admin-login-form">
            {error && (
              <div className="error-message">
                <span>⚠️</span> {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@milan-ai.net"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                '🔐 Continue'
              )}
            </button>

            <a href="/forgot-password?type=admin" className="forgot-password-link">
              Forgot Password?
            </a>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleOtpSubmit} className="admin-login-form otp-form">
            {error && (
              <div className="error-message">
                <span>⚠️</span> {error}
              </div>
            )}

            <div className="otp-info">
              <span className="otp-icon">📧</span>
              <p>We've sent a 6-digit OTP to</p>
              <strong>{adminEmail}</strong>
            </div>

            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  className="otp-input"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <button type="submit" className="login-btn" disabled={loading || otp.join('').length !== 6}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Verifying OTP...
                </>
              ) : (
                '✓ Verify & Login'
              )}
            </button>

            <div className="otp-actions">
              <button 
                type="button" 
                className="resend-btn"
                onClick={handleResendOtp}
                disabled={resendCooldown > 0 || loading}
              >
                {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : '📩 Resend OTP'}
              </button>
              <button 
                type="button" 
                className="back-btn"
                onClick={handleBackToCredentials}
              >
                ← Back to Login
              </button>
            </div>
          </form>
        )}

        {step === 'passkey' && (
          <div className="admin-login-form passkey-form">
            {error && (
              <div className="error-message">
                <span>⚠️</span> {error}
              </div>
            )}

            <div className="otp-info">
              <span className="otp-icon">{passkeyStage === 'register' ? '🛡️' : '🔑'}</span>
              {passkeyStage === 'register' ? (
                <>
                  <p>This is the final step. Register this device so only it can sign in.</p>
                  <strong>{adminEmail}</strong>
                  <p style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                    You'll be asked for Touch ID / your device unlock. Do this on the laptop you
                    want to keep as your admin device.
                  </p>
                </>
              ) : (
                <>
                  <p>Confirm it's you with your registered device passkey.</p>
                  <strong>{adminEmail}</strong>
                  <p style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                    Use Touch ID / your device unlock. This only works on the device you registered.
                  </p>
                </>
              )}
            </div>

            <button
              type="button"
              className="login-btn"
              onClick={handlePasskey}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Waiting for device…
                </>
              ) : passkeyStage === 'register' ? (
                '🛡️ Register this device'
              ) : (
                '🔑 Verify with passkey'
              )}
            </button>

            <div className="otp-actions">
              <button type="button" className="back-btn" onClick={handleBackToCredentials}>
                ← Back to Login
              </button>
            </div>
          </div>
        )}

        <div className="admin-login-footer">
          <p>🔒 Secured with 2-Factor Authentication</p>
          <a href="/" className="back-link">← Back to Milan AI</a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
